<template>
    <div class="sidebar" :class="{'active': showBurgerMenu}">
        <div class="position-sticky">
            <div class="sidebar-header">
                <a
                    :title="'Redirect on Dashboard page'"
                    :href="homeUrl"
                    class="navbar-brand sidebar_logo_well"
                    tabindex="1"
                >
                    <img :src="logoUrl" alt="CRBG Logo">
                </a>
                <button v-if="isMobile" @click="setShowBurgerMenu(!showBurgerMenu)" class="navbar-toggler" type="button">
                    <span class="navbar-toggler-icon"></span>
                </button>
            </div>
            <div class="sidebar-content">
                <ul v-if="user" class="navbar-nav flex-column">
                    <li
                        v-for="(item, key) in leftMenuItems"
                        :key="item.id"
                        class="nav-item tools-menu"
                        :class="{ 'dropdown': hasChildren(item), 'active': activeDropdown === item.id }"
                    >
                        <a
                            href="#"
                            v-if="hasChildren(item)"
                            @click.prevent="toggleDropdown(item.id)"
                            class="nav-link"
                            :tabindex="(1 + key) * 10"
                        >
                            {{ item.name }}
                            <span class="dropdown-icon"></span>
                        </a>
                        <a
                            v-else
                            :href="item.url"
                            :target="item.target === 'New Window' ? '_blank' : ''"
                            :tabindex="20+key"
                            @click="clearActiveItems"
                            class="nav-link"
                        >
                            {{ item.name }}
                        </a>

                        <div
                            v-if="hasChildren(item)"
                            :ref="'dropdownMenu-' + item.id"
                            :class="{
                            'show-no-animated': activeDropdown === item.id && isFirstLoaded,
                            'show': activeDropdown === item.id && !isFirstLoaded }"
                            class="tools-menu-header dropdown-menu"
                        >
                            <ul>
                                <li v-for="(child, childKey) in item.children" v-if="showItem(child)" :key="child.id">
                                    <a href=""
                                       :href="child.url"
                                       :target="child.target === 'New Window' ? '_blank' : ''"
                                       :title="child.tooltip || ''"
                                       :tabindex="((1 + key) * 10) + 1 + childKey"
                                       class="dropdown-item"
                                       @click="handleExternalClick($event, child)"
                                    >{{ child.name }}<span v-if="child.target === 'New Window'" class="external-icon"></span>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </li>
                </ul>
                <header-menu-component :class="{'mb-3': isMobile}" v-if="isMobile"></header-menu-component>
                <div v-if="user" class="sign-out">
                    <a
                        href="/logout"
                        class=""
                        tabindex="300"
                    >
                        Sign Out<span class="sign-icon"></span>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {mapActions, mapState} from 'vuex';
import HeaderMenuComponent from './HeaderMenuComponent.vue'

export default {
    name: 'LeftSidebarComponent',
    props: [
        'dailcareTermination'
    ],
    components: {
        HeaderMenuComponent
    },
    data() {
        return {
            homeUrl: '/',
            logoUrl: '/images/crbg/logo-compass-rose-health-plan.svg',
            isMobile: false,
            vueLoaded: false,
            isFirstLoaded: true,
            isInitialRouteLoad: true,
            errorResponse: [],
        }
    },
    mounted() {
        this.checkIsMobile()
        window.addEventListener('resize', this.checkIsMobile)

        this.vueLoaded = true;

        const staticSidebar = document.getElementById('static-sidebar');
        if (staticSidebar) staticSidebar.style.display = 'none';

        const vueSidebar = document.getElementById('vue-sidebar');
        if (vueSidebar) vueSidebar.style.display = 'block';

        this.openMenuItemRelatedToRoute();
        window.addEventListener('blur', this.clearHoverEffects);
        document.addEventListener('visibilitychange', this.clearHoverEffects);
    },
    watch: {
        $route() {
            this.openMenuItemRelatedToRoute();
        }
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.checkIsMobile)
        window.removeEventListener('blur', this.clearHoverEffects);
        document.removeEventListener('visibilitychange', this.clearHoverEffects);
    },
    computed: {
        ...mapState({
            showBurgerMenu: state => state.navigation.showBurgerMenu,
            user: state => state.user.user,
            activeDropdown: state => state.navigation.activeDropdown,
        }),
        leftMenuItems() {
            return this.user?.menus?.find(menu => menu.name === 'Left Navigation Menu')?.items || [];
        }
    },
    methods: {
        ...mapActions('navigation', ['setShowBurgerMenu', 'setActiveDropdown']),
        toggleDropdown(itemId) {
            if (!this.isInitialRouteLoad) {
                this.isFirstLoaded = false;
            }
            const isActive = this.activeDropdown === itemId;
            this.setActiveDropdown(isActive ? null : itemId);
            this.updateDropdownHeight();

            if (isActive) {
                this.clearActiveItems();
            }
        },
        updateDropdownHeight() {
            this.$nextTick(() => {
                this.leftMenuItems.forEach(item => {
                    const menuRef = this.$refs['dropdownMenu-' + item.id];
                    const menu = Array.isArray(menuRef) ? menuRef[0] : menuRef;
                    if (menu) {
                        menu.style.maxHeight = item.id === this.activeDropdown
                            ? menu.scrollHeight + 'px'
                            : 0
                    }
                });
            });
        },
        hasChildren(item) {
            return item.children && item.children.length > 0;
        },
        async signOut() {
            try {
                const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
                await fetch('/logout', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': csrfToken,
                    },
                    body: JSON.stringify({})
                });
                window.location.href = '/login';
            } catch (error) {
                console.error('Logout failed', error);
            }
        },
        isExternalLink(item) {
            try {
                const link = new URL(item.url);
                return link.hostname !== window.location.hostname || item.target === 'New Window';
            } catch (e) {
                return false;
            }
        },
        checkIsMobile() {
            this.isMobile = window.innerWidth <= 992;
        },
        openMenuItemRelatedToRoute() {
            this.clearActiveItems();

            const pathname = window.location.pathname;
            const segments = pathname.split('/');
            const lastSegment = segments.pop() || segments.pop();

            if (lastSegment && lastSegment !== 'dashboard') {
                this.leftMenuItems.forEach((item) => {
                    if (this.hasChildren(item)) {
                        item.children.forEach((child) => {
                            if (!child?.url) return;

                            const childSegment = new URL(child.url).pathname.split('/').pop();

                            if (childSegment === lastSegment) {
                                this.setActiveDropdown(item.id);
                                this.updateDropdownHeight();

                                const menuItem = document.querySelector(`.nav-item.tools-menu .dropdown-item[href="${child.url}"]`);
                                if (menuItem) {
                                    menuItem.classList.add('hover');
                                }

                                this.$nextTick(() => {
                                    this.isInitialRouteLoad = false;
                                });
                            }
                        });
                    }
                });
            }
        },
        clearHoverEffects() {
            const items = document.querySelectorAll('.dropdown-item');
            items.forEach(item => {
                // if (this.isExternalLink({ url: item.getAttribute('href'), target: item.getAttribute('target') })) {
                //     console.log(111)
                //     item.classList.add('force-clear-hover');
                // }

                if (item.getAttribute('target') === '_blank') item.classList.add('force-clear-hover');
            });
        },
        handleExternalClick(event, item) {
            this.clearActiveItems();

            if (item.name === 'Virtual Vet (DialCare)') {
                this.$http.get('/api/dialcare-sso/').then(function(response) {
                    window.open(response.body, '_blank');
                }, (response) => {
                    this.errorResponse = response;
                })
            } else if (item.target === 'New Window') {
                event.preventDefault();
                event.target.blur();
                event.target.classList.add('temp-no-hover');
                window.open(item.url, '_blank');
                setTimeout(() => {
                    event.target.classList.remove('temp-no-hover');
                }, 500);
            } else {
                event.target.classList.add('hover');
            }
        },
        clearActiveItems() {
            const activeItems = document.querySelectorAll('.sidebar .dropdown-item.hover');
            activeItems.forEach(item => {
                item.classList.remove('hover');
                item.classList.remove('force-clear-hover');
            });
        },
        showItem(item){
            if(item.name === 'Virtual Vet (DialCare)' && this.dailcareTermination && new Date() >= new Date(this.dailcareTermination)) {
                return false;
            }
            return true;
        }
    },
}
</script>

<style lang="scss" scoped>
.sidebar {
    .tools-menu-header {
        overflow: hidden;
        max-height: 0;
        padding: 0;
        background: #fff;
        box-shadow: none;

        &:not(.show-no-animated) {
            transition: max-height 0.3s ease;
        }

        &.dropdown-menu {
            li:last-child {
                padding-right: 0 !important;
            }
        }

        .dropdown-item {
            color: #00275D;
            font-size: 16px;
            font-weight: 500;
            padding: 10px 15px;
            display: block;

            &:hover {
                background: #F1F6FB;
                border-radius: 10px;
                color: #1169B1;
            }

            &:focus {
                outline: none !important;
            }

            &:active {
                background: #F1F6FB;
                color: #1169B1;
                border-radius: 10px;
            }

            &.hover {
                background: #F1F6FB;
                color: #1169B1 !important;
                border-radius: 10px;
            }

            &.temp-no-hover {
                background: transparent !important;
                color: #00275D !important;
                pointer-events: none;
            }
        }

        ul {
            list-style-type: none;
            padding: 0;
            margin: 0;
        }

        li {
            padding: 0;
            margin: 0;
        }
    }

    .tools-menu-header:not(.show) {
        animation-name: slideUp;
    }

    .tools-menu-header.show:not(.show-no-animated) {
        animation-name: slideDown;
    }

    .tools-menu-header.show-no-animated {
        animation: none;
        max-height: none !important;
    }
}

@keyframes slideDown {
    0% {
        max-height: 0;
        opacity: 1;
    }
    100% {
        max-height: 1000px;
        opacity: 1;
    }
}

@keyframes slideUp {
    0% {
        max-height: 1000px;
        opacity: 1;
    }
    100% {
        max-height: 0;
        opacity: 1;
    }
}

.dropdown-item.hover {
    background: #F1F6FB;
    color: #1169B1 !important;
    border-radius: 10px;
}
</style>
